import { useMemo } from "react";
import { Gauge } from "lucide-react";
import { TH, TD } from "../../../../components/ui";
import { TOOLS } from "../../../../mock/config.mock";
import NoDataState from "../../../../../components/NoDataState";
import SectionShell from "../SectionShell";
import useSectionDraft from "../useSectionDraft";
import { CellNumber } from "../cells";
import { SurfaceFilters, useSurfaceFilter } from "./SurfacesSection";

// Heatmap tint from the theme's primary colour — no hardcoded colours, so it
// follows light / dark mode automatically.
const tint = (v, max) => {
  if (!(v > 0) || !(max > 0)) return undefined;
  const pct = Math.round(6 + (v / max) * 44);
  return { backgroundColor: `color-mix(in srgb, var(--color-primary) ${pct}%, transparent)` };
};

export default function ProductivitySection({ config, readOnly, onSaved }) {
  // Productivity lives on each surface, so this section saves the surfaces slice.
  const draft = useSectionDraft(config, "surfaces", onSaved, "Productivity");
  const surfaces = draft.value;
  const filter = useSurfaceFilter(surfaces);
  const max = useMemo(
    () => Math.max(0, ...(surfaces || []).flatMap((s) => TOOLS.map((t) => Number(s.productivity?.[t]) || 0))),
    [surfaces]
  );

  if (!surfaces) return null;

  return (
    <SectionShell
      title="Productivity"
      subtitle="Square feet per labor hour, by surface and field tool. Darker = faster."
      icon={Gauge}
      draft={draft}
      readOnly={readOnly}
      actions={
        <div className="hidden items-center gap-1.5 text-[11px] text-text_muted md:flex" aria-hidden="true">
          <span>Slow</span>
          <span
            className="h-2 w-20 rounded-full border border-border_soft"
            style={{ background: "linear-gradient(90deg, color-mix(in srgb, var(--color-primary) 6%, transparent), color-mix(in srgb, var(--color-primary) 50%, transparent))" }}
          />
          <span>Fast</span>
        </div>
      }
    >
      <SurfaceFilters
        search={filter.search}
        onSearch={filter.setSearch}
        group={filter.group}
        onGroup={filter.setGroup}
        groups={filter.groups}
        count={filter.rows.length}
        total={surfaces.length}
      />

      {filter.rows.length === 0 ? (
        <NoDataState icon="search" title="No surfaces match" message="Try a different search term or clear the group filter." showBack={false} showRefresh={false} size="small" />
      ) : (
        <div className="max-h-[calc(100vh-330px)] overflow-auto rounded-[4px] border border-border">
          <table className="w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className={`${TH} sticky left-0 z-30 min-w-[260px]`}>Surface</th>
                {TOOLS.map((t) => (
                  <th key={t} className={`${TH} w-32 text-right`}>
                    {t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filter.rows.map(({ s, index }) => (
                <tr key={s.id}>
                  <th scope="row" className={`${TD} sticky left-0 z-10 bg-surface text-left font-medium`}>
                    {s.name}
                    <span className="ml-2 text-[11px] font-normal text-text_muted">{s.group}</span>
                  </th>
                  {TOOLS.map((t) => {
                    const v = Number(s.productivity?.[t]) || 0;
                    return (
                      <td key={t} className={`${TD} p-0`} style={tint(v, max)}>
                        <CellNumber
                          value={v}
                          onChange={(n) => draft.update((d) => { d[index].productivity[t] = n; })}
                          disabled={readOnly}
                          step="5"
                          min={0}
                          transparent
                          className="h-9 rounded-none font-medium"
                          ariaLabel={`${s.name} – ${t} SF per hour`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-2 text-[11.5px] text-text_muted">0 = tool not applicable for that surface (the engine falls back to Roller).</p>
    </SectionShell>
  );
}
